import React, {createRef, PropsWithChildren, ReactNode, RefObject} from "react";
import PortalDocument, {PortalDocumentInterface} from "../../../document/PortalDocument";
import {PdfPageDefinition} from "../PdfPage";
import LengthUnit from "../../../../shared/units/LengthUnit";
import CircleUnit from "../../../../shared/units/CircleUnit";
import DomHelper from "../../../../shared/helper/DomHelper";
import {PagePages} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import DateHelper from "../../../../shared/helper/DateHelper";
import I18n from "../../../../shared/translation/i18n";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";

interface AnnotationRectangleProperties extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	pageNumber: number;
	width?: number;
	height?: number;
	x?: number;
	y?: number;
	rotation?: number;
	zoomValue: number;
	popupContent?: string;
	popupCreator?: string;
	popupColor?: string;
	creationDate?: string;
	context: PortalContext;
	portalDocument: Immutable<PortalDocumentInterface>;
}

export class AnnotationRectangleDefinition extends PortalPureComponent<PropsWithChildren<AnnotationRectangleProperties>> {
	private popupElement: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();
	private rectangleElement: RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

	constructor(props: Readonly<PropsWithChildren<AnnotationRectangleProperties>>) {
		super(props);

		this.popupElement = createRef<HTMLDivElement>();
		this.rectangleElement = createRef<HTMLDivElement>();
	}

	private renderPopup(): ReactNode {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let page: PagePages | undefined = portalDocument.getPage(this.props.pageNumber);
		let scale: number = this.props.zoomValue / 100;
		let pageRotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);
		let popup: ReactNode = null;

		if (!FunctionHelper.isEmpty(this.props.popupContent) && !FunctionHelper.isEmpty(this.props.popupCreator)) {
			let popupLeft: string = "100%";
			let popupTop: string = "0";

			switch (pageRotation) {
				case 1:
					popupLeft = "0";
					break;
				case 2:
					popupLeft = "0";
					popupTop = "100%";
					break;
				case 3:
					popupTop = "100%";
					break;
			}

			let popupColor: string = FunctionHelper.applyValue(this.props.popupColor, "#ffffff");
			let colorRegex: RegExp = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
			let match: RegExpExecArray | null = colorRegex.exec(popupColor);

			if (match !== null) {
				let r: number = parseInt(match[1], 16);
				let g: number = parseInt(match[2], 16);
				let b: number = parseInt(match[3], 16);

				r = Math.floor(0.7 * (255 - r) + r);
				g = Math.floor(0.7 * (255 - g) + g);
				b = Math.floor(0.7 * (255 - b) + b);

				popupColor = '#' + r.toString(16) + g.toString(16) + b.toString(16);
			}

			popup = (
				<div className="popupWrapper" ref={this.popupElement} hidden={true} style={{
					transformOrigin: "0 0",
					transform: "rotate(" + (-pageRotation * 90) + "deg) scale(" + (1 / scale) + ")",
					left: popupLeft,
					top: popupTop
				}}>
					<div className="popup" style={{
						background: popupColor
					}}>
						<h1>{this.props.popupCreator}</h1>
						{
							FunctionHelper.exists(this.props.creationDate) &&
							!FunctionHelper.isEmpty(this.props.creationDate) &&
							<span className="popupDate">
								{DateHelper.buildTimestampString(new Date(this.props.creationDate!), I18n.language)}
							</span>
						}
						<div className="richText popupContent">
							<p>{this.props.popupContent}</p>
						</div>
					</div>
				</div>
			);
		}

		return popup;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument.get().id!
		);
		let page: PagePages | undefined = portalDocument.getPage(this.props.pageNumber);
		let scale: number = this.props.zoomValue / 100;
		let pageRotation: number = PdfPageDefinition.calculateRotationSteps(
			FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault())
		);
		let hasPopup: boolean = !FunctionHelper.isEmpty(this.props.popupContent) &&
			!FunctionHelper.isEmpty(this.props.popupCreator);

		let rotation: number = FunctionHelper.applyValue(page?.rotation, PagePages.getRotationDefault()) -
			(FunctionHelper.applyValue(this.props.rotation, PagePages.getRotationDefault()) * (180 / Math.PI));
		let width: number = FunctionHelper.applyValue(this.props.width, 0);
		let height: number = FunctionHelper.applyValue(this.props.height, 0);
		let posX: number = FunctionHelper.applyValue(this.props.x, 0);
		let posY: number = FunctionHelper.applyValue(this.props.y, 0);

		let rotationOffset: number = Math.abs(height - width) / 2;
		let originalX: number = posX + width / 2;
		let originalY: number = posY + height / 2;

		let translateX: number = 0;
		let translateY: number = 0;

		let left: string | undefined;
		let right: string | undefined;
		let top: string | undefined;
		let bottom: string | undefined;

		switch (pageRotation) {
			case 0:
				left = this.props.x + LengthUnit.PIXEL;
				bottom = this.props.y + LengthUnit.PIXEL;
				translateX = originalX * scale - originalX;
				translateY = (originalY * scale - originalY) * -1;
				break;
			case 1:
				top = this.props.x + LengthUnit.PIXEL;
				left = this.props.y + LengthUnit.PIXEL;
				translateX = originalY * scale - originalY;
				translateY = originalX * scale - originalX;

				if (width > height) {
					translateX -= rotationOffset;
					translateY += rotationOffset;
				} else {
					translateX += rotationOffset;
					translateY -= rotationOffset;
				}
				break;
			case 2:
				right = this.props.x + LengthUnit.PIXEL;
				top = this.props.y + LengthUnit.PIXEL;
				translateX = (originalX * scale - originalX) * -1;
				translateY = originalY * scale - originalY;
				break;
			case 3:
				bottom = this.props.x + LengthUnit.PIXEL;
				right = this.props.y + LengthUnit.PIXEL;
				translateX = (originalY * scale - originalY) * -1;
				translateY = (originalX * scale - originalX) * -1;

				if (width > height) {
					translateX += rotationOffset;
					translateY -= rotationOffset;
				} else {
					translateX -= rotationOffset;
					translateY += rotationOffset;
				}
				break;
		}

		return (
			<>
				<div className={this.props.className} ref={this.rectangleElement}
					 onClick={(event) => {
						 if (FunctionHelper.exists(this.props.onClick)) {
							 this.props.onClick!(event);
						 }

						 if (!FunctionHelper.exists(this.popupElement.current) ||
							 !FunctionHelper.exists(this.rectangleElement.current)) {
							 return;
						 }

						 let pinned: string | null = DomHelper.data(this.popupElement.current!, 'pinned');
						 DomHelper.data(this.popupElement.current!, 'pinned', String(pinned !== 'true'));

						 if (pinned === 'true') {
							 this.popupElement.current!.setAttribute('hidden', "true");
							 this.rectangleElement.current!.style.zIndex = "0";
						 } else {
							 this.popupElement.current!.removeAttribute('hidden');
							 this.rectangleElement.current!.style.zIndex = "1";
						 }
					 }}
					 onPointerOver={() => {
						 if (!FunctionHelper.exists(this.popupElement.current) ||
							 !FunctionHelper.exists(this.rectangleElement.current)) {
							 return;
						 }

						 this.popupElement.current!.removeAttribute('hidden');
						 this.rectangleElement.current!.style.zIndex = "1";
					 }}
					 onPointerOut={() => {
						 if (!FunctionHelper.exists(this.popupElement.current) ||
							 !FunctionHelper.exists(this.rectangleElement.current)) {
							 return;
						 }

						 let pinned: string | null = DomHelper.data(this.popupElement.current!, 'pinned');
						 if (pinned !== 'true') {
							 this.popupElement.current!.setAttribute('hidden', "true");
							 this.rectangleElement.current!.style.zIndex = "0";
						 }
					 }}
					 style={{
						 left: left,
						 right: right,
						 top: top,
						 bottom: bottom,
						 width: this.props.width + LengthUnit.PIXEL,
						 height: this.props.height + LengthUnit.PIXEL,
						 cursor: hasPopup === null ? "auto" : "pointer",
						 transform:
							 "translate(" + translateX + LengthUnit.PIXEL + ", " + translateY + LengthUnit.PIXEL + ") " +
							 "rotate(" + rotation + CircleUnit.DEGREES + ") " +
							 "scale(" + scale + ")",
						 ...this.props.style
					 }}>
					{this.props.children}
					{this.renderPopup()}
				</div>
			</>
		);
	}
}

const AnnotationRectangle = React.memo(AnnotationRectangleDefinition,
	(prevProps: Readonly<AnnotationRectangleProperties>, nextProps: Readonly<AnnotationRectangleProperties>) => {
		if (!nextProps.portalDocument.equals(prevProps.portalDocument)) {
			return false;
		}

		if (nextProps.pageNumber !== prevProps.pageNumber) {
			return false;
		}

		if (nextProps.width !== prevProps.width) {
			return false;
		}

		if (nextProps.height !== prevProps.height) {
			return false;
		}

		if (nextProps.x !== prevProps.x) {
			return false;
		}

		if (nextProps.y !== prevProps.y) {
			return false;
		}

		if (nextProps.rotation !== prevProps.rotation) {
			return false;
		}

		if (nextProps.zoomValue !== prevProps.zoomValue) {
			return false;
		}

		if (nextProps.popupContent !== prevProps.popupContent) {
			return false;
		}

		if (nextProps.popupCreator !== prevProps.popupCreator) {
			return false;
		}

		if (nextProps.popupColor !== prevProps.popupColor) {
			return false;
		}

		if (nextProps.className !== prevProps.className) {
			return false;
		}

		return true;
	}
);

export default AnnotationRectangle;